/**
 * Dark-deploy module switchboard — registry, fallback and the pure resolver.
 *
 * Design: docs/categorized/architecture/DARK-DEPLOY-MODULE-SWITCHES-DESIGN.md
 * (§2 inventory, §3a resolution). This file is deliberately side-effect free
 * and dependency free: the frontend imports it through the @newgenus/common
 * barrel; the backend keeps a MIRROR of the constants (Cloud Functions cannot
 * import @newgenus/common at runtime — esbuild `bundle:false`) guarded by a
 * drift-guard spec, exactly like `wizardLimits`.
 *
 * Firestore document: `config/modules` (sibling of `config/featureFlags`).
 * Readable by any signed-in user, writable by admins only.
 */

/** Product modules (what a user sees) — order = the design's §2a table. */
export const PRODUCT_MODULE_KEYS = [
    'statistics',
    'client-abc',
    'organization',
    'workspace',
    'search',
    'triage',
    'calendar',
    'notifications',
    'policies',
    'invitations',
    'org-creation-wizard',
    'checklist',
    'management-tools',
    'user-settings',
    'test',
    'issue-tracker',
    'sales-performance',
] as const;

/** Platform modules (no user-facing switch, still need a dark position) — §2b. */
export const PLATFORM_MODULE_KEYS = [
    'task-engine',
    'denormalize',
    'basket-counts',
    'error-queue',
    'heartbeat',
    'admin',
] as const;

export const MODULE_KEYS = [...PRODUCT_MODULE_KEYS, ...PLATFORM_MODULE_KEYS] as const;

export type ProductModuleKey = (typeof PRODUCT_MODULE_KEYS)[number];
export type PlatformModuleKey = (typeof PLATFORM_MODULE_KEYS)[number];
export type ModuleKey = (typeof MODULE_KEYS)[number];

export const PLATFORM_MODULES: ReadonlySet<ModuleKey> = new Set<ModuleKey>(PLATFORM_MODULE_KEYS);

/**
 * ISS-026 (Paul, 2026-10-28): product modules open to EVERY member of an org where they are
 * switched on — tier 3 is skipped. Everyone may log issues, and the bell carries the updates
 * about them. Their feature keys stay registered so routes and menus still map to the module.
 */
export const MEMBER_OPEN_MODULE_KEYS = ['issue-tracker', 'notifications'] as const;
export const MEMBER_OPEN_MODULES: ReadonlySet<ModuleKey> = new Set<ModuleKey>(MEMBER_OPEN_MODULE_KEYS);

/**
 * Tier 3: the per-user feature keys (users/{uid}.organizations[org].features)
 * a product module is granted through. A user holding ANY of them has the
 * module. An empty list = public (any signed-in user). Platform modules have no
 * user tier.
 */
export const MODULE_FEATURE_KEYS: Readonly<Record<ModuleKey, readonly string[]>> = {
    statistics: ['statistics'],
    'client-abc': ['client-abc'],
    organization: [
        'organization',
        'organization-dashboard',
        'organization-settings',
        'organization-dates',
        'organization-targets',
        'organization-integrations',
    ],
    workspace: ['workspace'],
    search: ['search'],
    triage: ['triage'],
    calendar: ['calendar'],
    notifications: ['notifications'],
    policies: ['policies'],
    invitations: ['invitations'],
    'org-creation-wizard': ['orgCreationWizard'],
    checklist: ['checklist'],
    'management-tools': ['management-tools', 'management-tools-dashboard'],
    'user-settings': [],
    test: ['test'],
    'issue-tracker': ['issues'],
    'sales-performance': ['sales-performance'],
    // platform — no user tier
    'task-engine': [],
    denormalize: [],
    'basket-counts': [],
    'error-queue': [],
    heartbeat: [],
    admin: [],
};

export interface ModuleSwitch {
    /** Tier 1 — global. `false` = dark everywhere (developers included unless allowDevelopers). */
    enabled: boolean;
    /** Tier 2 — org allow-list. Empty/absent = every org (when enabled). */
    orgs?: string[];
    /** Lets `isDeveloper` users see this module while it is DARK. Default false (Paul, 4 Sep 2026). */
    allowDevelopers?: boolean;
    /** When this switch last changed (Firestore Timestamp or ISO string — opaque here). */
    since?: unknown;
    note?: string;
}

export interface ModulesDoc {
    /** Bumped on every write; clients and the backend cache key on it. */
    version: number;
    modules: Partial<Record<ModuleKey, ModuleSwitch>>;
}

const on = (extra?: Partial<ModuleSwitch>): ModuleSwitch => ({
    enabled: true,
    orgs: [],
    allowDevelopers: false,
    ...extra,
});
const off = (extra?: Partial<ModuleSwitch>): ModuleSwitch => ({
    enabled: false,
    orgs: [],
    allowDevelopers: false,
    ...extra,
});

/**
 * What a MISSING `config/modules` document resolves to: production's surface
 * as it is today (measured 2026-09-04 on the sandbox replica). An outage of the
 * config read must fail closed to "prod as it is", never to "everything on".
 */
export const PROD_SURFACE_FALLBACK: ModulesDoc = Object.freeze({
    version: 0,
    modules: Object.freeze({
        statistics: on(),
        'client-abc': on(),
        organization: on(), // the page routes 13 prod users hold; the board itself is gated by the bare `organization` key nobody holds
        workspace: on(), // 2 prod users
        search: off(),
        triage: off(),
        calendar: off(),
        notifications: off(),
        policies: off(),
        invitations: off(),
        'org-creation-wizard': off(),
        checklist: on(),
        'management-tools': on(),
        'user-settings': on(),
        test: off(),
        'issue-tracker': off(), // dark on prod by construction until the migration (ISSUE-TRACKER-PRD.md §2)
        'sales-performance': off(), // OE-293 prototype — dark until Flash signs it off
        'task-engine': on(),
        denormalize: on(),
        'basket-counts': on(),
        'error-queue': on(),
        heartbeat: on(),
        admin: on(),
    }),
}) as ModulesDoc;

/** Null/undefined document → the production-surface fallback; otherwise the document itself. */
export function resolveModulesDoc(doc: ModulesDoc | null | undefined): ModulesDoc {
    return doc ?? PROD_SURFACE_FALLBACK;
}

export interface ModuleContext {
    /** The selected org. Omit for org-less callers (crons) — then only tier 1 applies. */
    orgId?: string;
    /** The user's per-org feature keys. Omit for backend callers with no user — then tier 3 is skipped. */
    userFeatures?: readonly string[];
    isDeveloper?: boolean;
}

/** The tier-2 allow-list for a module ([] = every org). */
export function moduleOrgs(doc: ModulesDoc | null | undefined, module: ModuleKey): string[] {
    const sw = resolveModulesDoc(doc).modules[module];
    return sw?.orgs ? [...sw.orgs] : [];
}

/**
 * The one resolution rule, identical on both surfaces:
 *
 *   isOn = enabled
 *       && (orgs empty || orgs includes orgId)            — only when an orgId is given
 *       && (public || user holds a feature key || isDeveloper) — only when userFeatures is given
 *
 * plus the developer rule: a developer sees a DARK module only when the module
 * says `allowDevelopers: true`. Unknown module ⇒ OFF.
 */
export function isModuleOn(doc: ModulesDoc | null | undefined, module: ModuleKey, ctx: ModuleContext = {}): boolean {
    const sw = resolveModulesDoc(doc).modules[module];
    if (!sw) return false; // unregistered ⇒ dark by construction

    if (!sw.enabled) {
        return !!(sw.allowDevelopers && ctx.isDeveloper);
    }

    if (ctx.orgId !== undefined && sw.orgs && sw.orgs.length > 0 && !sw.orgs.includes(ctx.orgId)) {
        return false;
    }

    const userFeatures = ctx.userFeatures;
    if (userFeatures !== undefined && !PLATFORM_MODULES.has(module)) {
        if (ctx.isDeveloper) return true;
        if (MEMBER_OPEN_MODULES.has(module)) return true;
        const keys = MODULE_FEATURE_KEYS[module] ?? [];
        if (keys.length === 0) return true; // public module
        return keys.some((k) => userFeatures.includes(k));
    }

    return true;
}
